// ============================================================================
//  Official date/time formatting utilities — matches the mobile (Expo) app 1:1.
//  All outputs use Latin digits (0-9), YYYY-MM-DD dates, and 24-hour HH:mm.
// ============================================================================

export const AR_DAYS = [
  "الأحد",     // 0 — Sunday
  "الإثنين",   // 1 — Monday
  "الثلاثاء",  // 2
  "الأربعاء",  // 3
  "الخميس",    // 4
  "الجمعة",    // 5
  "السبت",     // 6 — Saturday
];

export const EN_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function pad(n) {
  const num = Number(n);
  return num < 10 ? `0${num}` : `${num}`;
}

// Normalise any Arabic-Indic digits to Latin 0-9.
export function toLatinDigits(input) {
  if (input === null || input === undefined) return "";
  return String(input)
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d).toString())
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d).toString());
}

// Coerce Date | ISO string | "YYYY-MM-DD" | "HH:mm" → Date (or null if bad).
// Uses LOCAL timezone (avoids the ISO-string UTC shift bug for date-only values).
function toDate(input) {
  if (!input && input !== 0) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const raw = toLatinDigits(String(input)).trim();
  // Bare YYYY-MM-DD? Parse as local (append T00:00 to avoid UTC shift).
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const d = new Date(`${raw}T00:00:00`);
    return isNaN(d.getTime()) ? null : d;
  }
  // Bare HH:mm[:ss]? Not a date on its own.
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(raw)) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

// 1) formatDate → "2026-06-29"
export function formatDate(input) {
  if (!input) return "";
  const d = toDate(input);
  if (!d) return toLatinDigits(String(input)).slice(0, 10);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// 2) formatTime → "15:30" (24h)
export function formatTime(input) {
  if (!input && input !== 0) return "";
  // Accept bare "HH:mm[:ss]" directly (no Date parse needed)
  const s = toLatinDigits(String(input)).trim();
  const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (m) {
    const hh = pad(m[1]);
    const mm = m[2];
    return `${hh}:${mm}`;
  }
  const d = toDate(input);
  if (!d) return "";
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// 3) formatDateTime → "2026-06-29 15:30"
export function formatDateTime(input) {
  if (!input) return "";
  const d = toDate(input);
  if (!d) return toLatinDigits(String(input));
  return `${formatDate(d)} ${formatTime(d)}`;
}

// 4) formatDateBilingual → "الأحد · 2026-06-29"
export function formatDateBilingual(input) {
  if (!input) return "";
  const d = toDate(input);
  if (!d) return toLatinDigits(String(input));
  return `${AR_DAYS[d.getDay()]} · ${formatDate(d)}`;
}

// 5) formatFull → "الأحد · 2026-06-29 · 15:30"
export function formatFull(input) {
  if (!input) return "";
  const d = toDate(input);
  if (!d) return toLatinDigits(String(input));
  return `${formatDateBilingual(d)} · ${formatTime(d)}`;
}

// 6) parseDate — build a Date from "YYYY-MM-DD" + "HH:mm"
export function parseDate(dateStr, timeStr = "00:00") {
  if (!dateStr) return new Date();
  const cleanDate = toLatinDigits(String(dateStr)).slice(0, 10);
  const cleanTime = toLatinDigits(String(timeStr)).slice(0, 5) || "00:00";
  const d = new Date(`${cleanDate}T${cleanTime}:00`);
  return isNaN(d.getTime()) ? new Date() : d;
}
